import React, { createContext, useContext, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ReactMarkdown, { type Components } from 'react-markdown';
import type { Root } from 'hast';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import {
  annotateAdvisoryMarkdownQuotes,
  findAdvisoryQuoteAnchors,
  type AdvisoryQuoteReference,
} from './advisoryQuoteAnchors';

import type { AdvisoryTableSourceReference } from './advisoryTableSources';

interface InlineEvidenceContext {
  references: readonly AdvisoryQuoteReference[];
  tableReferences?: readonly AdvisoryTableSourceReference[];
  onOpenTableSource?: (reference: AdvisoryTableSourceReference, origin: HTMLElement) => void;
  onOpen: (reference: AdvisoryQuoteReference, origin: HTMLElement) => void;
}

const EvidenceContext = createContext<InlineEvidenceContext>({
  references: [],
  onOpen: () => undefined,
});
export const AdvisoryInlineEvidenceProvider = EvidenceContext.Provider;

/** Shared appearance; a source link carries no quotation check or quote index. */
function EvidenceChip({
  label,
  absent = false,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string;
  absent?: boolean;
}) {
  return (
    <Box
      component="button"
      type="button"
      aria-haspopup="dialog"
      {...props}
      sx={theme => ({
        display: 'inline-block',
        verticalAlign: '2px',
        marginX: '3px',
        padding: '1px 7px 2px',
        borderRadius: theme.visualPrimitives.radiusPill,
        border: `1px solid ${alpha(
          absent ? theme.colors.vermilion : theme.colors.lavenderDeep,
          0.5
        )}`,
        backgroundColor: 'transparent',
        color: absent ? theme.colors.vermilion : theme.colors.lavenderDeep,
        fontFamily: theme.visualPrimitives.monoStack,
        fontSize: 11,
        fontWeight: 500,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        '&:hover': { backgroundColor: alpha(theme.colors.lavender, 0.16) },
        '&:focus-visible': { outline: `2px solid ${theme.colors.lavenderDeep}`, outlineOffset: 3 },
        '@media print': { border: 0, padding: 0 },
      })}
    >
      {label}
    </Box>
  );
}

function QuoteChip({ reference }: { reference: AdvisoryQuoteReference }) {
  const { t } = useUntypedTranslation();
  const { onOpen } = useContext(EvidenceContext);
  const status = t(`advisory:brief.sources.quotes.status.${reference.quote.status}`);
  return (
    <EvidenceChip
      label={reference.label}
      absent={reference.quote.status === 'absent'}
      data-advisory-quote-ref={reference.key}
      data-quote-index={reference.quoteIndex}
      data-quote-check={reference.quote.status}
      aria-label={t('advisory:brief.sources.quoteLink', {
        label: reference.label,
        source: reference.source.title ?? t('advisory:brief.sources.untitled'),
        status,
      })}
      title={`${reference.quote.text} — ${status}`}
      onClick={event => onOpen(reference, event.currentTarget)}
    />
  );
}

/** Keep a table's explicit source attached to its claim, even for paraphrases. */
export function AdvisoryTableSourceCitations({ source }: { source: string | null | undefined }) {
  const { t } = useUntypedTranslation();
  const { tableReferences = [], onOpenTableSource } = useContext(EvidenceContext);
  const references = tableReferences.filter(reference =>
    reference.sourceValues.includes(source?.trim() ?? '')
  );
  if (!references.length || !onOpenTableSource) {
    return (
      <Typography
        component="span"
        variant="inherit"
        sx={theme => ({ color: theme.colors.inkTertiary, fontStyle: 'italic' })}
      >
        {' '}
        {t('advisory:brief.sources.tableSourceUnavailable')}
      </Typography>
    );
  }
  return (
    <>
      {references.map(reference => (
        <EvidenceChip
          key={reference.key}
          label={reference.label}
          data-advisory-table-source-ref={reference.key}
          aria-label={t('advisory:brief.sources.tableSourceLink', {
            label: reference.label,
            source: reference.source.title ?? t('advisory:brief.sources.untitled'),
          })}
          title={t('advisory:brief.sources.tableSourceHint')}
          onClick={event => onOpenTableSource(reference, event.currentTarget)}
        />
      ))}
    </>
  );
}

/** Plain values in tables, lists, summaries and specialized components. */
export const AdvisoryEvidenceText = React.memo(function AdvisoryEvidenceText({
  children,
  renderText,
}: {
  children: string | null | undefined;
  renderText?: (text: string) => React.ReactNode;
}) {
  const text = children ?? '';
  const { references } = useContext(EvidenceContext);
  const anchors = findAdvisoryQuoteAnchors(text, references);
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  anchors.forEach(anchor => {
    const fragment = text.slice(cursor, anchor.end);
    parts.push(renderText ? renderText(fragment) : fragment);
    parts.push(
      <QuoteChip key={`${anchor.reference.key}-${anchor.end}`} reference={anchor.reference} />
    );
    cursor = anchor.end;
  });
  const remainder = text.slice(cursor);
  parts.push(renderText ? renderText(remainder) : remainder);
  return <>{parts}</>;
});

/**
 * A recorded value with its source markers, or the fixed copy for an absent one.
 * Every renderer goes through this so a value can never lose its markers by
 * being written out directly.
 */
export const AdvisoryEvidenceValue = React.memo(function AdvisoryEvidenceValue({
  children,
}: {
  children: string | null | undefined;
}) {
  const { t } = useUntypedTranslation();
  return (children ?? '').trim() ? (
    <AdvisoryEvidenceText>{children}</AdvisoryEvidenceText>
  ) : (
    <Typography
      component="span"
      variant="inherit"
      sx={theme => ({ color: theme.colors.inkTertiary, fontStyle: 'italic' })}
    >
      {t('advisory:brief.notProvided')}
    </Typography>
  );
});

// Keep the component type stable across live-data refreshes so the exact quote
// button stays mounted. Read current references through context instead of a closure.
const markdownComponents: Components = {
  sup: function MarkdownQuote({ node, children, ...props }) {
    const { references } = useContext(EvidenceContext);
    const key = node?.properties.dataAdvisoryQuote;
    const reference = references.find(candidate => candidate.key === key);
    return reference ? <QuoteChip reference={reference} /> : <sup {...props}>{children}</sup>;
  },
};

/** Match rendered text across emphasis while keeping links and code non-interactive. */
export const AdvisoryEvidenceMarkdown = React.memo(function AdvisoryEvidenceMarkdown({
  children,
}: {
  children: string;
}) {
  const { references } = useContext(EvidenceContext);
  const plugin = useMemo(
    () => () => (tree: Root) => annotateAdvisoryMarkdownQuotes(tree, references),
    [references]
  );
  return (
    <ReactMarkdown rehypePlugins={[plugin]} components={markdownComponents}>
      {children}
    </ReactMarkdown>
  );
});
